import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModal } from '../contexts/ModalContext'; // Corrected import path
import vanImage from '../assets/images/Carini-Homes.gif'; // Assuming GIF, or use .jpg
import pejmanBgImage from '../assets/images/pejman.png'; // Import pejman.png
import heroBackgroundImage from '../assets/images/hero-background.jpg'; // Import hero background

// Import actual icons
import iconAC from '../assets/images/icons/AirConditioning.svg';
import iconHeat from '../assets/images/icons/HeatingSolutions.svg';
import iconMaint from '../assets/images/icons/MaintenancePlans.svg';
import iconIAQ from '../assets/images/icons/IndoorAirQuality.svg';
import icon247 from '../assets/images/icons/247EmergencyService.svg';
import iconCert from '../assets/images/icons/CertifiedTechnicians.svg';
import iconPrice from '../assets/images/icons/UpfrontFairPricing.svg';
import iconGuarantee from '../assets/images/icons/SatisfactionGuaranteed.svg';

// Import testimonial images
import testimonialJohnD from '../assets/images/testimonials/JohnD.png';
import testimonialSarahP from '../assets/images/testimonials/SarahP.png'; // Added Sarah P.
import testimonialMikeB from '../assets/images/testimonials/MikeB.png';   // Added Mike B.

// Placeholder for icons - consider using a library like react-icons
// For example: import { FaSnowflake, FaFire, FaShieldAlt, FaWind, FaClock, FaUserShield, FaDollarSign, FaAward, FaStar } from 'react-icons/fa';
// const IconPlaceholder = ({ name, className = 'w-12 h-12 text-brand-green-medium mb-4' }) => <div className={`mx-auto ${className}`}>[{name}]</div>; // Removed IconPlaceholder

const StarRating = ({ rating }) => {
  return (
    <div className="flex justify-center mb-2">
      {[...Array(5)].map((_, index) => {
        // Replace with <FaStar /> or similar from react-icons
        const starClass = index < rating ? 'text-brand-accent-yellow' : 'text-gray-300';
        return <span key={index} className={`w-5 h-5 ${starClass}`}>★</span>; 
      })}
    </div>
  );
};

const HeroSection = () => {
  const { t } = useTranslation();
  const { openModal } = useModal(); // Get openModal function

  return (
    <section 
      className="text-white relative text-center min-h-[70vh] overflow-hidden pt-[90px] pb-[38px] md:pb-[54px] bg-cover bg-center"
      style={{ backgroundImage: `url(${heroBackgroundImage})` }} // Applied background image
    >
      <div className="absolute inset-0 bg-brand-green-dark opacity-60 z-0"></div> {/* Overlay for text readability */}
      
      {/* Animated Van Image */}
      <img 
        src={vanImage} 
        alt={t('home.hero.van_alt')} 
        className="absolute bottom-[-18px] left-1/4 w-auto h-1/4 md:h-1/1 max-w-[500px] md:max-w-[500px] z-22 animate-vanPatrol" // Used new animation
      />

      {/* Hero Text Container with Background */}
      <div className="relative z-5 w-full bg-black bg-opacity-5 rounded-lg shadow-xl">
        <div className="container mx-auto container-padding py-4 md:py-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-brand-white filter drop-shadow-lg hero-headline-3d">
            {t('home.hero.title')}
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 text-brand-off-white max-w-3xl mx-auto filter drop-shadow-md">
            {t('home.hero.subtitle')}
          </p>
          <div>
            <button 
              onClick={() => openModal('scheduleService')} // Updated to open modal
              className="cta-button text-lg sm:text-xl py-3 px-8 sm:py-4 sm:px-10 shadow-sm hover:shadow-md transition-shadow"
            >
              {t('home.hero.cta_button')}
            </button>
            {/* <Link to="/request-quote" className="cta-button-secondary text-lg sm:text-xl py-3 px-8 sm:py-4 sm:px-10 ml-4">
              Get a Free Estimate
            </Link> */}
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesOverviewSection = () => {
  const { t } = useTranslation();
  const services = [
    {
      name: t('home.services.air_conditioning.name'),
      description: t('home.services.air_conditioning.description'),
      link: "/services/ac-services",
      icon: iconAC
    },
    {
      name: t('home.services.heating.name'),
      description: t('home.services.heating.description'),
      link: "/services/heating-services",
      icon: iconHeat
    },
    {
      name: t('home.services.maintenance.name'),
      description: t('home.services.maintenance.description'),
      link: "/services/maintenance-plans",
      icon: iconMaint
    },
    {
      name: t('home.services.air_quality.name'),
      description: t('home.services.air_quality.description'),
      link: "/services/air-quality",
      icon: iconIAQ
    }
  ];

  return (
    <section className="bg-brand-off-white section-padding">
      <div className="container mx-auto container-padding text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-text-dark mb-4">
          {t('home.services.title')}
        </h2>
        <p className="text-lg text-brand-text-light mb-12 max-w-2xl mx-auto">
          {t('home.services.subtitle')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service) => (
            <Link to={service.link} key={service.name} className="block p-6 bg-brand-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1 border border-gray-200">
              <img src={service.icon} alt={`${service.name} icon`} className="w-12 h-12 text-brand-green-medium mb-4 mx-auto" />
              <h3 className="text-xl font-semibold text-brand-green-medium mb-2">{service.name}</h3>
              <p className="text-sm text-brand-text-light mb-4 text-left">{service.description}</p>
              <span className="inline-block mt-auto cta-button-secondary text-sm py-2 px-4">
                {t('home.services.learn_more')} &rarr;
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyChooseUsSection = () => {
  const { t } = useTranslation();
  const reasons = [
    {
      title: t('home.why_choose.emergency_service.title'),
      description: t('home.why_choose.emergency_service.description'),
      icon: icon247
    },
    {
      title: t('home.why_choose.certified_techs.title'),
      description: t('home.why_choose.certified_techs.description'),
      icon: iconCert
    },
    {
      title: t('home.why_choose.fair_pricing.title'),
      description: t('home.why_choose.fair_pricing.description'),
      icon: iconPrice
    },
    {
      title: t('home.why_choose.satisfaction.title'),
      description: t('home.why_choose.satisfaction.description'),
      icon: iconGuarantee
    }
  ];

  return (
    <section className="section-padding bg-gray-50">
      <div className="container mx-auto container-padding text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-text-dark mb-4">
          {t('home.why_choose.title')}
        </h2>
        <p className="text-lg text-brand-text-light mb-12 max-w-2xl mx-auto">
          {t('home.why_choose.subtitle')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {reasons.map((reason) => (
            <div key={reason.title} className="p-6 bg-brand-off-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <img src={reason.icon} alt={`${reason.title} icon`} className="w-12 h-12 text-brand-accent-orange mb-4 mx-auto" />
              <h3 className="text-xl font-semibold text-brand-green-dark mb-2">{reason.title}</h3>
              <p className="text-sm text-brand-text-light text-left">{reason.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TestimonialsSnippetSection = () => {
  const { t } = useTranslation();
  const testimonials = [
    {
      quote: t('home.testimonials.john_quote'),
      name: t('home.testimonials.john_name'),
      location: t('home.testimonials.john_location'),
      rating: 5,
      image: testimonialJohnD
    },
    {
      quote: t('home.testimonials.sarah_quote'),
      name: t('home.testimonials.sarah_name'),
      location: t('home.testimonials.sarah_location'),
      rating: 5,
      image: testimonialSarahP // Added Sarah P. image
    },
    {
      quote: "We had a new heating system installed by Quality Air, and the whole process was seamless. Great communication and a very fair price. Our home is so much warmer now!",
      name: "Mike B.",
      location: "Sugar Land, TX",
      rating: 5,
      image: testimonialMikeB // Added Mike B. image
    }
  ];

  return (
    <section className="section-padding bg-brand-off-white">
      <div className="container mx-auto container-padding text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-text-dark mb-4">
          Hear From Our Satisfied Houston Customers
        </h2>
        <p className="text-lg text-brand-text-light mb-12 max-w-2xl mx-auto">
          We take pride in delivering top-notch service and ensuring our customers are happy. See what they have to say!
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-brand-white p-6 md:p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col justify-between items-center">
              {testimonial.image && (
                <img 
                  src={testimonial.image} 
                  alt={testimonial.name} 
                  className="w-24 h-24 rounded-full mb-4 object-cover shadow-sm"
                />
              )}
              <div>
                <StarRating rating={testimonial.rating} />
                <blockquote className="text-brand-text-light italic mb-6 text-center text-base leading-relaxed">
                  <p>"{testimonial.quote}"</p>
                </blockquote>
              </div>
              <div className="text-center">
                <p className="font-semibold text-brand-green-medium">{testimonial.name}</p>
                <p className="text-sm text-gray-500">{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/testimonials" className="cta-button-secondary">
            Read More Testimonials
          </Link>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  return (
    <div className=""> {/* Removed padding top here as it's handled by HeroSection now */}
      {/* <EmergencyCallout /> Rendered in App.js now */}
      <HeroSection />
      <ServicesOverviewSection />
      <WhyChooseUsSection />
      <TestimonialsSnippetSection />
    </div>
  );
};  


export default HomePage; 